"use client";

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatPercent } from "@/lib/utils";

export function EvidenceCompare({
  originalUrl,
  targetUrl,
  originalScreenshot,
  targetScreenshot,
  scores,
  matchingText,
  matchingHtml,
  matchingAssets,
  sha256,
  httpStatus,
  capturedAt,
  immutable,
}: {
  originalUrl: string;
  targetUrl: string;
  originalScreenshot?: string | null;
  targetScreenshot?: string | null;
  scores: { text: number; dom: number; asset: number; visual: number; overall: number };
  matchingText: string[];
  matchingHtml: string[];
  matchingAssets: { kind: string; value: string }[];
  sha256: string;
  httpStatus: number | null;
  capturedAt: string;
  immutable: boolean;
}) {
  const [panel, setPanel] = useState<"text" | "images" | "html" | "hash" | "shots" | null>("shots");

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Pane
          title="Original site"
          url={originalUrl}
          screenshot={originalScreenshot}
        />
        <Pane title="Suspect site" url={targetUrl} screenshot={targetScreenshot} />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Score label="Overall" value={scores.overall} />
        <Score label="Text" value={scores.text} />
        <Score label="Layout" value={scores.dom} />
        <Score label="Files" value={scores.asset} />
        <Score label="Look" value={scores.visual} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={() => setPanel("text")}>
          Matching text
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setPanel("images")}>
          Matching images
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setPanel("html")}>
          Matching page parts
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setPanel("hash")}>
          Evidence fingerprint
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setPanel("shots")}>
          Screenshots
        </Button>
      </div>
      {panel === "text" ? <List title="Matching text" items={matchingText} /> : null}
      {panel === "html" ? <List title="Matching page parts" items={matchingHtml} /> : null}
      {panel === "images" ? (
        <List
          title="Matching images"
          items={matchingAssets.filter((a) => a.kind === "image").map((a) => a.value)}
        />
      ) : null}
      {panel === "hash" ? (
        <Card>
          <CardContent className="space-y-2 font-mono text-xs">
            <p>Fingerprint: {sha256}</p>
            <p>HTTP status: {httpStatus ?? "none"}</p>
            <p>Captured: {capturedAt}</p>
            <p>Locked: {immutable ? "yes" : "no"}</p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function Pane({
  title,
  url,
  screenshot,
}: {
  title: string;
  url: string;
  screenshot?: string | null;
}) {
  return (
    <Card>
      <CardContent>
        <p className="text-xs text-[var(--text-faint)]">{title}</p>
        <p className="mt-1 truncate text-sm text-[var(--accent)]">{url}</p>
        <div className="field-3d mt-3 overflow-hidden">
          {screenshot ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={`/api/files/${screenshot}`} alt={title} className="w-full" />
          ) : (
            <div className="flex h-48 items-center justify-center text-sm text-[var(--text-muted)]">
              No screenshot yet. The page content is still saved.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Score({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent>
        <p className="text-xs text-[var(--text-muted)]">{label}</p>
        <p className="text-xl font-semibold">{formatPercent(value)}</p>
      </CardContent>
    </Card>
  );
}

function List({ title, items }: { title: string; items: string[] }) {
  return (
    <Card>
      <CardContent>
        <p className="mb-2 font-medium">{title}</p>
        {items.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">No matches.</p>
        ) : (
          <ul className="space-y-1 text-sm text-[var(--text-muted)]">
            {items.map((item) => (
              <li key={item} className="field-3d px-2 py-1 font-mono text-xs">
                {item}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
